/* Sprint 2R-3 (hybrid pre-pilot) — Admin Performance Dashboard.
 *
 * Aggregated table of all approved drivers with v2-score, status badge,
 * trend, no-show + scan-mismatch + warnings counters, preferred postcodes.
 * Watchlist/high-risk filter at top. Click driver row → opens history modal.
 */
import React from "react";
import { adminApi } from "../../lib/api";
import { Loader2, Activity, AlertTriangle, TrendingUp, TrendingDown, Minus, RefreshCw, X, History as HistoryIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  good: { bg: "bg-emerald-100", text: "text-emerald-700", label: "OK" },
  watchlist: { bg: "bg-amber-100", text: "text-amber-800", label: "WATCHLIST" },
  high_risk: { bg: "bg-red-100", text: "text-red-700", label: "HIGH RISK" },
  no_data: { bg: "bg-slate-100", text: "text-slate-600", label: "INGEN DATA" },
};

const TREND_ICON = {
  improving: TrendingUp,
  declining: TrendingDown,
  stable: Minus,
  no_data: Minus,
};

const TREND_COLOR = {
  improving: "text-emerald-600",
  declining: "text-red-600",
  stable: "text-slate-500",
  no_data: "text-slate-400",
};

export default function PerformanceDashboardPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [filter, setFilter] = React.useState("all");
  const [days, setDays] = React.useState(30);
  const [openDriver, setOpenDriver] = React.useState(null);

  const load = React.useCallback(async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const filterParam = filter === "all" ? "" : `&status_v2=${filter}`;
      const r = await adminApi().get(`/admin/performance/dashboard?days=${days}${filterParam}`);
      setData(r.data);
    } catch (e) {
      toast.error("Kunne ikke hente performance dashboard");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [filter, days]);

  React.useEffect(() => { load(false); }, [load]);

  if (loading) return (
    <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser performance...</div>
  );

  return (
    <div className="space-y-6" data-testid="performance-dashboard-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-xl text-brand-dark flex items-center gap-2">
            <Activity size={20} className="text-brand-red"/> Performance Dashboard
          </h2>
          <p className="text-sm text-brand-text2 mt-1">
            Operationel performance per chauffør (sidste {days} dage). Ingen revenue / margin / Stripe / Billy data eksponeres.
          </p>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
                className="px-4 py-2 bg-white border border-brand hover:bg-brand-bg2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="perf-dashboard-refresh">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""}/> Opdater
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="perf-summary">
        {[
          { k: "total", label: "I alt", color: "bg-slate-700 text-white", val: data?.summary?.total || 0 },
          { k: "good", label: "OK", color: "bg-emerald-600 text-white", val: data?.summary?.good || 0 },
          { k: "watchlist", label: "Watchlist", color: "bg-amber-500 text-white", val: data?.summary?.watchlist || 0 },
          { k: "high_risk", label: "High Risk", color: "bg-red-600 text-white", val: data?.summary?.high_risk || 0 },
          { k: "no_data", label: "Ingen data", color: "bg-slate-400 text-white", val: data?.summary?.no_data || 0 },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setFilter(s.k === "total" ? "all" : s.k)}
            className={`${s.color} p-3 text-left ${filter === (s.k === "total" ? "all" : s.k) ? "ring-2 ring-brand-dark" : ""}`}
            data-testid={`perf-filter-${s.k}`}
          >
            <div className="text-2xl font-bold">{s.val}</div>
            <div className="text-xs uppercase tracking-wide">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Days selector */}
      <div className="flex items-center gap-3 text-sm">
        <span className="text-brand-text2">Periode:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 ${days === d ? "bg-brand-red text-white" : "bg-white border border-brand text-brand-dark hover:bg-brand-bg2"}`}
            data-testid={`perf-days-${d}`}
          >
            {d} dage
          </button>
        ))}
        {filter !== "all" && (
          <button onClick={() => setFilter("all")}
                  className="ml-auto text-xs text-brand-text2 hover:text-brand-dark inline-flex items-center gap-1">
            <X size={12}/> Ryd filter ({filter})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-brand overflow-x-auto">
        <table className="w-full text-sm" data-testid="perf-table">
          <thead>
            <tr className="bg-brand-bg2 border-b border-brand text-left text-xs uppercase text-brand-text2">
              <th className="p-3">Chauffør</th>
              <th className="p-3">Vognmand</th>
              <th className="p-3 text-center">Score</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-center">Trend</th>
              <th className="p-3 text-center">Leveret</th>
              <th className="p-3 text-center">Fejl%</th>
              <th className="p-3 text-center">On-time%</th>
              <th className="p-3 text-center">No-shows</th>
              <th className="p-3 text-center">Mismatch</th>
              <th className="p-3 text-center">Advarsler</th>
              <th className="p-3">Foretrukne postnr</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(data?.items || []).length === 0 && (
              <tr><td colSpan={13} className="p-8 text-center text-brand-text2">Ingen chauffører matcher filteret</td></tr>
            )}
            {(data?.items || []).map((d) => {
              const badge = STATUS_BADGE[d.status_v2] || STATUS_BADGE.no_data;
              const TrendI = TREND_ICON[d.trend] || Minus;
              return (
                <tr key={d.driver_id} className="border-b border-gray-100 hover:bg-brand-bg2 cursor-pointer"
                    onClick={() => setOpenDriver(d.driver_id)}
                    data-testid={`perf-row-${d.driver_id}`}>
                  <td className="p-3">
                    <div className="font-semibold text-brand-dark">{d.name}</div>
                    <div className="text-xs text-brand-text2">{d.email}</div>
                  </td>
                  <td className="p-3 text-xs">{d.subcontractor_name || <span className="text-brand-text2">—</span>}</td>
                  <td className="p-3 text-center">
                    <div className="text-xl font-bold">{d.score_v2 != null ? d.score_v2 : "—"}</div>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-[10px] uppercase font-bold px-2 py-1 ${badge.bg} ${badge.text}`}>{badge.label}</span>
                  </td>
                  <td className={`p-3 text-center ${TREND_COLOR[d.trend] || "text-slate-400"}`}>
                    <TrendI size={16} className="inline"/>
                  </td>
                  <td className="p-3 text-center">{d.delivered_count || 0}</td>
                  <td className="p-3 text-center">
                    {d.fail_rate != null ? <span className={d.fail_rate > 0.1 ? "text-red-600 font-semibold" : ""}>{(d.fail_rate * 100).toFixed(1)}</span> : "—"}
                  </td>
                  <td className="p-3 text-center">
                    {d.on_time_rate != null ? <span className={d.on_time_rate < 0.85 ? "text-amber-600 font-semibold" : ""}>{(d.on_time_rate * 100).toFixed(0)}</span> : "—"}
                  </td>
                  <td className="p-3 text-center">
                    {d.no_show_count > 0 ? <span className="text-red-600 font-bold">{d.no_show_count}</span> : "0"}
                  </td>
                  <td className="p-3 text-center">
                    {d.scan_mismatch_count > 0 ? <span className="text-amber-600 font-bold">{d.scan_mismatch_count}</span> : "0"}
                  </td>
                  <td className="p-3 text-center">
                    {d.warnings_count > 0 ? <span className="text-red-600 font-bold">{d.warnings_count}</span> : "0"}
                  </td>
                  <td className="p-3 text-xs">
                    {d.preferred_postcodes && d.preferred_postcodes.length > 0
                      ? d.preferred_postcodes.slice(0,3).map(p => p.postal_code).join(", ")
                      : <span className="text-brand-text2">—</span>}
                  </td>
                  <td className="p-3 text-center">
                    <HistoryIcon size={14} className="text-brand-text2"/>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {openDriver && <DriverHistoryModal driverId={openDriver} onClose={() => setOpenDriver(null)}/>}
    </div>
  );
}

function DriverHistoryModal({ driverId, onClose }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await adminApi().get(`/admin/perf/drivers/${driverId}/history?days=90`);
        setData(r.data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [driverId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 pb-12 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl mx-4 border border-brand shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-brand-dark">Chauffør-historik (90 dage)</h3>
          <button onClick={onClose} data-testid="driver-history-close"><X size={18}/></button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto" data-testid="driver-history-content">
          {loading ? (
            <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={14}/> Indlæser...</div>
          ) : !data ? (
            <div className="text-sm text-brand-text2">Kunne ikke hente</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <HistoryGroup label="No-shows" items={data.no_shows} render={(n) => (
                <div>
                  <div className="font-mono text-xs">{n.route_id?.slice(0,8)}</div>
                  <div className="text-xs text-brand-text2">{new Date(n.marked_at_iso).toLocaleDateString("da-DK")} · {n.fine_dkk} DKK · {n.status}</div>
                  <div className="text-xs">{n.reason}</div>
                </div>
              )}/>
              <HistoryGroup label="Scan-mismatches" items={data.scan_mismatches} render={(m) => (
                <div>
                  <div className="font-mono text-xs">{m.kind}</div>
                  <div className="text-xs text-brand-text2">{new Date(m.at_iso).toLocaleDateString("da-DK")} · {m.status}</div>
                  {m.label_number && <div className="text-xs font-mono">{m.label_number}</div>}
                </div>
              )}/>
              <HistoryGroup label="Advarsler" items={data.warnings} render={(w) => (
                <div>
                  <div className="text-xs font-semibold">{w.severity || "advarsel"}</div>
                  <div className="text-xs text-brand-text2">{new Date(w.at_iso).toLocaleDateString("da-DK")}</div>
                  <div className="text-xs">{w.reason}</div>
                </div>
              )}/>
              <HistoryGroup label="Tildelings-overrides" items={data.assignment_overrides} render={(o) => (
                <div>
                  <div className="text-xs font-mono">{o.route_id?.slice(0,8)}</div>
                  <div className="text-xs text-brand-text2">{new Date(o.at_iso).toLocaleDateString("da-DK")} · {o.status_v2_at_assignment}</div>
                  <div className="text-xs">{o.override_reason}</div>
                </div>
              )}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function HistoryGroup({ label, items, render }) {
  return (
    <div>
      <div className="text-xs uppercase text-brand-text2 mb-2 font-semibold">{label} ({items?.length || 0})</div>
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {(!items || items.length === 0) && <div className="text-xs text-brand-text2">Ingen i perioden</div>}
        {items?.map((it, i) => (
          <div key={it.id || i} className="p-2 border border-gray-100">
            {render(it)}
          </div>
        ))}
      </div>
    </div>
  );
}
